import React, { ReactNode } from "react";
import { Page } from "../../activities/AppPage";
import { API } from "../../API";
import {
  Agency,
  AgencyWithIsChatOnly,
  ClockedInStatus,
  ClockInOutCallType,
  PatientTaskInstance,
  VisitInstanceClockinClockoutRequestParams,
  VisitInstanceDetails,
} from "../../messages";
import { CaregiverId, PatientTaskInstanceId, VisitInstanceId } from "../../messages/ObjectIds";
import { formatPhone } from "../../phone_number";
import { STRINGS } from "../../strings";
import { Patient } from "../../system/AppState/AppPatientsState";
import { translate } from "../../translate";
import { getFullName } from "../../utils/getFullName";
import { withDefault } from "../../utils/withDefault";
import { useClockInOutModals } from "../ClockInOutModals";
import ContactAgency from "../ContactAgency/ContactAgency";
import AppSlideFadeIn from "../Generic/AppSlideFadeIn";
import Flex from "../Generic/Flex";
import { IsDefined } from "../Generic/IsDefined";
import ListCard from "../ListCard";
import { PatientDirectivesCard } from "../PatientDirectivesCard";
import { PatientMedicationsCard } from "../PatientMedicationsCard";
import Scaffold from "../Scaffold/Scaffold";
import CallPatientBottomSheet from "./components/CallPatientBottomSheet";
import DirectionsBottomSheet from "./components/DirectionsBottomSheet";
import EmergencyPreparednessCard from "./components/EmergencyPreparednessCard";
import ListOfTasksCard from "./components/ListOfTasksCard";
import ListOfVisitsCard from "./components/ListOfVisitsCard";
import NotesCard from "./components/NotesCard";
import PatientDetailsCard from "./components/PatientDetailsCard";
import PatientScreenExtraHeader from "./components/PatientScreenExtraHeader";
import PatientVitalsCards from "./components/PatientVitalsCards";
import { OutsourcingAgencyBanner } from "../OutsourcingAgencyBanner/OutsourcingAgencyBanner";

interface Props {
  patient: Patient;
  agency: AgencyWithIsChatOnly;
  isCallOpen: boolean;
  isDirectionsOpen: boolean;
  tasksPreview: PatientTaskInstance[];
  clockedInStatus: ClockedInStatus;
  caregiverId: CaregiverId;
  isCaregiverOfMedflyt: boolean;
  visitLetsStartBottomSheet: ReactNode;
  outsourcingAgencyData: Agency | null;
  onRequestNavigate: (page: Page) => void;
  onClock: (
    visitInstanceId: VisitInstanceId,
    clockInOutCallType: ClockInOutCallType,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams
  ) => Promise<boolean>;
  onRequestCloseCallPatient: () => void;
  onRequestCloseDirections: () => void;
  onClickChat: () => void;
  onClickCallPatient: () => void;
  onClickPatientPhone: (phoneNumber: string) => void;
  onClickDirections: () => void;
  onClickListOfVisits: () => void;
  onClickListOfTasks: () => void;
  onClickTask: (
    taskInstanceId: PatientTaskInstanceId,
    initialScheduleDateTimePickerOpen: boolean
  ) => void;
  onClickChatWithAgency: () => void;
  onClickCallAgency: () => void;
  onRequestOpenVisitLetsStartBottomSheet: (visitInstance: VisitInstanceDetails) => void;
  onClickGoBack: () => void;
  onUploadVerificationSelfie: (
    visitInstanceId: VisitInstanceId,
    imageBase64: string,
    clockType: ClockInOutCallType
  ) => Promise<void>;
  onRequestVisitAdjustmentCheck: API["getCaregiverCanAdjustVisitInstanceTimes"];
  onCheckIfDocumentsSubmitted: API["checkIfAllDocumentsSubmitted"];
  onCaregiverMarkVisitAsMissed: API["caregiverMarkVisitAsMissed"];
  onRequestOutsourcingAgencyData: API["getOutsourcingAgencyData"];
}

const PatientScreen = (props: Props) => {
  const handleClock = (
    visitInstanceId: VisitInstanceId,
    visitInstanceClockinClockoutRequestParams: VisitInstanceClockinClockoutRequestParams,
    type: ClockInOutCallType
  ) => {
    return props.onClock(visitInstanceId, type, visitInstanceClockinClockoutRequestParams);
  };

  const handleRequireManualClock = (visitInstanceId: VisitInstanceId) => {
    props.onRequestNavigate({
      type: "TimesheetActivity",
      visitInstanceId: visitInstanceId,
    });
  };

  const onClickClockIn = (visitDetails: VisitInstanceDetails) => {
    promptClock({
      clockedInStatus: props.clockedInStatus,
      visitInstance: visitDetails,
      clockType: ClockInOutCallType.IN,
    });
  };

  const onClickClockOut = (visitDetails: VisitInstanceDetails) => {
    promptClock({
      clockedInStatus: props.clockedInStatus,
      visitInstance: visitDetails,
      clockType: ClockInOutCallType.OUT,
    });
  };

  const {
    actions: { promptClock },
  } = useClockInOutModals({
    caregiverId: props.caregiverId,
    isCaregiverOfMedflyt: props.isCaregiverOfMedflyt,
    onClickClock: handleClock,
    onRequireManualClock: handleRequireManualClock,
    onClockPresentVisit: props.onRequestOpenVisitLetsStartBottomSheet,
    onUploadVerificationSelfie: props.onUploadVerificationSelfie,
    onRequestVisitAdjustmentCheck: props.onRequestVisitAdjustmentCheck,
    onCheckIfDocumentsSubmitted: props.onCheckIfDocumentsSubmitted,
    onRequestMissedVisit: props.onCaregiverMarkVisitAsMissed,
  });

  const extraHeader = React.useMemo(() => {
    return (
      <AppSlideFadeIn>
        <PatientScreenExtraHeader
          patient={props.patient}
          onClickChat={props.onClickChat}
          onClickCallPatient={props.onClickCallPatient}
          onClickDirections={props.onClickDirections}
        />
      </AppSlideFadeIn>
    );
  }, [props.patient, props.onClickChat, props.onClickCallPatient, props.onClickDirections]);

  return (
    <Scaffold
      className="PatientScreen"
      extraHeader={extraHeader}
      onClickGoBack={props.onClickGoBack}
    >
      <CallPatientBottomSheet
        isOpen={props.isCallOpen}
        homePhone={props.patient.homePhoneNumber}
        mobilePhone={props.patient.mobilePhoneNumber}
        onClickPhone={props.onClickPatientPhone}
        onRequestClose={props.onRequestCloseCallPatient}
      />

      <DirectionsBottomSheet
        isOpen={props.isDirectionsOpen}
        destination={withDefault(props.patient.address, props.patient.address2)}
        onRequestClose={props.onRequestCloseDirections}
      />

      <Flex direction="column" yGap={16}>
        {/* outsourcing agency banner */}
        <AppSlideFadeIn delay={550}>
          <OutsourcingAgencyBanner
            onRequestOutsourcingAgencyData={props.onRequestOutsourcingAgencyData}
            patientId={props.patient.id}
            context="Patient"
          />
        </AppSlideFadeIn>
        {/* List of Visits */}
        <AppSlideFadeIn delay={600}>
          <ListOfVisitsCard
            title={translate(STRINGS.LIST_OF_VISITS)}
            onClick={props.onClickListOfVisits}
          />
        </AppSlideFadeIn>

        {/* Required Tasks */}
        <AppSlideFadeIn delay={650}>
          <ListOfTasksCard
            tasksPreview={props.tasksPreview}
            clockedInStatus={props.clockedInStatus}
            onClickTask={props.onClickTask}
            onClick={props.onClickListOfTasks}
            onClickStartNow={props.onRequestOpenVisitLetsStartBottomSheet}
            onClickClockIn={onClickClockIn}
            onClickClockOut={onClickClockOut}
          />
        </AppSlideFadeIn>

        {/* Patient Details */}
        <AppSlideFadeIn delay={700}>
          <PatientDetailsCard
            dateOfBirth={props.patient.dateOfBirth}
            gender={props.patient.gender}
            homePhoneNumber={props.patient.homePhoneNumber}
            mobilePhoneNumber={props.patient.mobilePhoneNumber}
            phone3={props.patient.phone3}
            phone4={props.patient.phone4}
          />
        </AppSlideFadeIn>

        {/* Medical Notes */}
        <AppSlideFadeIn delay={750}>
          <NotesCard title={translate(STRINGS.MEDICAL_NOTES)} notes={props.patient.medicalNotes} />
        </AppSlideFadeIn>

        {/* General Notes */}
        <AppSlideFadeIn delay={800}>
          <NotesCard title={translate(STRINGS.GENERAL_NOTES)} notes={props.patient.generalNotes} />
        </AppSlideFadeIn>

        {/* Vitals (Blood Pressure, Temperature, Pulse, Respiration Rate) */}
        <AppSlideFadeIn delay={850}>
          <PatientVitalsCards vitals={props.patient.latestVitals} />
        </AppSlideFadeIn>

        {/* Emergency Preparedness */}
        <AppSlideFadeIn delay={900}>
          <EmergencyPreparednessCard
            electricEquipmentDependency={props.patient.electricEquipmentDependency}
            evacuationLocation={props.patient.evacuationLocation}
            evacuationZone={props.patient.evacuationZone}
            mobilityStatus={props.patient.mobilityStatus}
            priorityCode={props.patient.priorityCode}
          />
        </AppSlideFadeIn>

        <AppSlideFadeIn delay={950}>
          {props.patient.allergies !== null && props.patient.allergies.length > 0 ? (
            <ListCard
              title={translate(STRINGS.ALLERGIES)}
              items={props.patient.allergies.map((allergy) => allergy.name)}
            />
          ) : (
            <NotesCard title={translate(STRINGS.ALLERGIES)} notes={null} />
          )}
        </AppSlideFadeIn>

        <AppSlideFadeIn delay={1000}>
          {props.patient.medications !== null && props.patient.medications.length > 0 ? (
            <PatientMedicationsCard medications={props.patient.medications} />
          ) : (
            <NotesCard title={translate(STRINGS.MEDICATIONS)} notes={null} />
          )}
        </AppSlideFadeIn>
        <AppSlideFadeIn delay={1050}>
          {props.patient.emergencyContact !== null && props.patient.emergencyContact.length > 0 ? (
            <ListCard
              title={translate(STRINGS.EMERGENCY_CONTACTS)}
              items={props.patient.emergencyContact.map(
                (con) => `${getFullName(con)} - ${formatPhone(con.emergencyPhoneNumber1)}`
              )}
            />
          ) : (
            <NotesCard title={translate(STRINGS.EMERGENCY_CONTACTS)} notes={null} />
          )}
        </AppSlideFadeIn>
        <AppSlideFadeIn delay={1100}>
          {props.patient.advancedDirectives !== null ? (
            <PatientDirectivesCard advancedDirectives={props.patient.advancedDirectives} />
          ) : (
            <NotesCard title={translate(STRINGS.ADVANCED_DIRECTIVES)} notes={null} />
          )}
        </AppSlideFadeIn>

        {/* Contact Agency */}
        <IsDefined value={props.agency.officePhone}>
          {() => (
            <AppSlideFadeIn delay={1150}>
              <ContactAgency
                agency={props.outsourcingAgencyData ?? props.agency}
                onClickChat={props.onClickChatWithAgency}
                onClickCall={props.onClickCallAgency}
                showCallButton={props.outsourcingAgencyData === null}
              />
            </AppSlideFadeIn>
          )}
        </IsDefined>
      </Flex>

      {props.visitLetsStartBottomSheet}
    </Scaffold>
  );
};

export default React.memo(PatientScreen);
